import { TypedDocumentNode, gql } from '@apollo/client';
import { useMemo, useState } from 'react';

import { idFromObject } from '@sorare/core/src/gql/idFromObject';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';

import { GamesNavigator as DumbGamesNavigator } from '@sorare/gameplay/src/components/seo/GamesNavigator';
import { PlayerGameScoreDialog } from '@sorare/gameplay/src/components/stats/PlayerGameScoreDialog';
import { anyGameToGenericGame } from '@sorare/gameplay/src/lib/anyGame';
import { anyPlayerGameStatsToGenericGame } from '@sorare/gameplay/src/lib/anyPlayerGameStats';

import {
  BaseballPlayerGamesNavigatorQuery,
  BaseballPlayerGamesNavigatorQueryVariables,
} from './__generated__/index.graphql';

const BASEBALL_PLAYER_GAMES_NAVIGATOR_QUERY = gql`
  query BaseballPlayerGamesNavigatorQuery(
    $slug: String!
    $lastPast: Int!
    $nextFuture: Int!
  ) {
    anyPlayer(slug: $slug) {
      slug
      anyGameStats(last: $lastPast) {
        id
        ...anyPlayerGameStatsToGenericGame_anyPlayerGameStats
      }
      activeClub {
        slug
        upcomingAnyGames(first: $nextFuture) {
          id
          ...anyGameToGenericGame_anyGameInterface
        }
      }
    }
  }
  ${anyGameToGenericGame.fragments.anyGameInterface}
  ${anyPlayerGameStatsToGenericGame.fragments.anyPlayerGameStats}
` as TypedDocumentNode<
  BaseballPlayerGamesNavigatorQuery,
  BaseballPlayerGamesNavigatorQueryVariables
>;

type Props = { playerSlug: string };

const PAGE_SIZE = 5;
const PAST_GAMES_ON_FIRST_PAGE = 1;
const FUTURE_GAMES_ON_FIRST_PAGE = 4;
const SAFE_MAX_PAGE = 10;
export const GamesNavigator = ({ playerSlug }: Props) => {
  const [playerGameScoreId, setPlayerGameScoreId] = useState<string>();
  const [page, setPage] = useState(0);
  const { data, loading } = useQuery(BASEBALL_PLAYER_GAMES_NAVIGATOR_QUERY, {
    variables: {
      slug: playerSlug,
      lastPast:
        page <= 0 ? Math.abs(page * PAGE_SIZE) + PAST_GAMES_ON_FIRST_PAGE : 0,
      nextFuture: page >= 0 ? page * PAGE_SIZE + FUTURE_GAMES_ON_FIRST_PAGE : 0,
    },
  });

  const anyGameStats = data?.anyPlayer?.anyGameStats.filter(Boolean);

  const pastPlayerGameStatsToDisplay = useMemo(() => {
    if (!anyGameStats) return [];
    if (page > 0) return [];

    if (page === 0) {
      return anyGameStats.slice(0, PAST_GAMES_ON_FIRST_PAGE);
    }
    return anyGameStats.slice(
      PAGE_SIZE * (Math.abs(page) - 1) + PAST_GAMES_ON_FIRST_PAGE,
      PAGE_SIZE * Math.abs(page) + PAST_GAMES_ON_FIRST_PAGE
    );
  }, [anyGameStats, page]);

  const upcomingAnyGames =
    data?.anyPlayer?.activeClub?.upcomingAnyGames.filter(Boolean);
  const futureGamesToDisplay = useMemo(() => {
    if (!upcomingAnyGames) return [];
    if (page < 0) return [];
    if (page === 0) {
      return upcomingAnyGames.slice(0, FUTURE_GAMES_ON_FIRST_PAGE);
    }
    return upcomingAnyGames.slice(
      PAGE_SIZE * (page - 1) + FUTURE_GAMES_ON_FIRST_PAGE,
      PAGE_SIZE * page + FUTURE_GAMES_ON_FIRST_PAGE
    );
  }, [upcomingAnyGames, page]);

  return (
    <>
      <DumbGamesNavigator
        hasNextPage={
          page < SAFE_MAX_PAGE &&
          (page < 0 ||
            futureGamesToDisplay.length === PAGE_SIZE ||
            (page === 0 &&
              futureGamesToDisplay.length === FUTURE_GAMES_ON_FIRST_PAGE))
        }
        hasPreviousPage={
          Math.abs(page) < SAFE_MAX_PAGE &&
          (page > 0 ||
            pastPlayerGameStatsToDisplay.length === PAGE_SIZE ||
            (page === 0 &&
              pastPlayerGameStatsToDisplay.length === PAST_GAMES_ON_FIRST_PAGE))
        }
        loading={loading}
        loadPage={direction =>
          setPage(currentPage => currentPage + (direction === 'next' ? 1 : -1))
        }
        pastGames={pastPlayerGameStatsToDisplay.map(playerGameStats =>
          anyPlayerGameStatsToGenericGame({
            playerGameStats,
            onClick: id => setPlayerGameScoreId(idFromObject(id)),
          })
        )}
        futureGames={futureGamesToDisplay.map(anyGame =>
          anyGameToGenericGame({ anyGame })
        )}
      />
      {playerGameScoreId && (
        <PlayerGameScoreDialog
          playerGameScoreId={playerGameScoreId}
          onClose={() => setPlayerGameScoreId(undefined)}
          open={!!playerGameScoreId}
          showUpsell={false}
        />
      )}
    </>
  );
};
